import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { SALON_NAME } from '../services/api';
import { engagementPath } from '../engagementPaths';
import './VoiceCall.css';

// AI Voice Call (opened from the presentation page): the Sarvam phone agent rings the number typed here,
// the screen follows the call live, and opens Call Records when it ends.
const ASSET = (path) => `${process.env.PUBLIC_URL || ''}${path}`;
const POLL_MS = 2000;
const REDIRECT_SECONDS = 4;

const STAGES = {
  requesting: { label: 'Placing the call…', tone: 'live' },
  calling: { label: 'Calling…', tone: 'live', hint: 'Your phone is ringing. Pick up to talk to the AI assistant.' },
  in_progress: { label: 'Call in progress', tone: 'live', hint: 'You are talking to the AI assistant.' },
  completed: { label: 'Call finished', tone: 'done' },
  no_answer: { label: 'Not answered', tone: 'warn', hint: 'The call was not picked up.' },
  busy: { label: 'Line busy', tone: 'warn', hint: 'The number was busy.' },
  failed: { label: 'Call failed', tone: 'error' },
};
const FINAL = ['completed', 'no_answer', 'busy', 'failed'];

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
    </svg>
  );
}

function clock(seconds) {
  const s = Math.max(0, seconds || 0);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export default function VoiceCall() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [call, setCall] = useState(null); // { attemptId, dial, state, seconds, reason }
  const [error, setError] = useState('');
  const [setupMissing, setSetupMissing] = useState([]);
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const pollRef = useRef(null);

  useEffect(() => {
    document.title = `${SALON_NAME} — AI Voice Call`;
    api
      .outboundCallConfig()
      .then((c) => setSetupMissing(c?.configured ? [] : c?.missing || []))
      .catch(() => {});
    return () => clearInterval(pollRef.current);
  }, []);

  // Follow the call until it ends.
  useEffect(() => {
    if (!call?.attemptId || FINAL.includes(call.state)) return undefined;
    pollRef.current = setInterval(() => {
      api
        .outboundCallStatus(call.attemptId)
        .then((s) => setCall((c) => (c ? { ...c, state: s.state, seconds: s.seconds, reason: s.reason } : c)))
        .catch(() => {});
    }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [call?.attemptId, call?.state]);

  // Local timer between polls, so the clock ticks every second.
  useEffect(() => {
    if (!call || !['calling', 'in_progress'].includes(call.state)) return undefined;
    const t = setInterval(() => setCall((c) => (c ? { ...c, seconds: (c.seconds || 0) + 1 } : c)), 1000);
    return () => clearInterval(t);
  }, [call?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Finished: open Call Records after a short countdown.
  useEffect(() => {
    if (call?.state !== 'completed') return undefined;
    setCountdown(REDIRECT_SECONDS);
    const t = setInterval(() => setCountdown((n) => n - 1), 1000);
    return () => clearInterval(t);
  }, [call?.state]);
  useEffect(() => {
    if (call?.state === 'completed' && countdown <= 0) navigate(engagementPath('call-records'));
  }, [countdown, call?.state, navigate]);

  const digits = phone.replace(/\D/g, '');
  const busy = call && !FINAL.includes(call.state);
  const canCall = digits.length >= 10 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canCall) return;
    // A number without a country code is Indian.
    const dial = phone.trim().startsWith('+') ? phone.trim() : digits.length === 10 ? `+91${digits}` : `+${digits}`;
    setError('');
    setCall({ attemptId: null, dial, state: 'requesting', seconds: 0 });
    try {
      const res = await api.startOutboundCall(dial);
      setCall({ attemptId: res.attempt_id || null, dial: res.phone, state: 'calling', seconds: 0 });
    } catch (err) {
      setCall(null);
      setError(
        err.status === 404
          ? 'Customer engagement needs a service refresh. Use Refresh engagement service on this page, then try the call again.'
          : err.message || 'The call could not be placed.',
      );
    }
  };

  const reset = () => {
    clearInterval(pollRef.current);
    setCall(null);
    setError('');
  };

  const stage = call ? STAGES[call.state] || STAGES.calling : null;

  return (
    <div className="vc-page" style={{ backgroundImage: `url(${ASSET('/engagement/presentation-bg-naturals.png')})` }}>
      <div className="vc-scrim" />
      <div className="vc-card">
        <img className="vc-logo" src={ASSET('/engagement/naturals%20nxt.jpg')} alt={SALON_NAME} />
        <div className={`vc-icon${busy ? ' vc-icon--live' : ''}`}><PhoneIcon /></div>
        <h1 className="vc-title">AI Voice Call</h1>

        {!call ? (
          <>
            <p className="vc-sub">Enter your phone number. Our AI assistant will call you right away.</p>
            <form className="vc-form" onSubmit={submit}>
              <label className="vc-label" htmlFor="vc-phone">Phone number</label>
              <div className="vc-phone">
                {!phone.trim().startsWith('+') && <span className="vc-cc">+91</span>}
                <input
                  id="vc-phone"
                  className="vc-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^\d+\s-]/g, ''))}
                  placeholder="98765 43210"
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={20}
                />
              </div>
              <div className="vc-hint">Other countries: start with + and the country code.</div>

              <button type="submit" className="vc-btn" disabled={!canCall}>
                <PhoneIcon />
                <span>Call me</span>
              </button>
            </form>
            {error && <div className="vc-status vc-status--error" role="alert">{error}</div>}
            {setupMissing.length > 0 && !error && (
              <div className="vc-status vc-status--error">
                Voice calling is not set up yet. Missing in backend/.env: {setupMissing.join(', ')}
              </div>
            )}
          </>
        ) : (
          <div className="vc-live" role="status" aria-live="polite">
            <div className="vc-live-who">{call.dial}</div>
            <div className={`vc-badge vc-badge--${stage.tone}`}>
              {stage.tone === 'live' && <span className="vc-dot" />}
              {stage.label}
            </div>
            {['calling', 'in_progress'].includes(call.state) && <div className="vc-timer">{clock(call.seconds)}</div>}
            {stage.hint && <p className="vc-sub">{stage.hint}</p>}
            {call.state === 'failed' && call.reason && <p className="vc-sub">{call.reason}</p>}

            {call.state === 'completed' && (
              <>
                <p className="vc-sub">Opening Call Records in {Math.max(countdown, 0)}…</p>
                <button type="button" className="vc-btn" onClick={() => navigate(engagementPath('call-records'))}>View call records</button>
              </>
            )}
            {['no_answer', 'busy', 'failed'].includes(call.state) && (
              <button type="button" className="vc-btn" onClick={reset}>
                <PhoneIcon />
                <span>Try again</span>
              </button>
            )}
            {call.state === 'calling' && !call.attemptId && (
              <p className="vc-hint">Live status is not available for this call; check Call Records when it ends.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
